import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type GetQuoteResult, type ListUnitsResult } from '@whinerp/client';
import {
  Button,
  CapabilityUnavailable,
  EmptyState,
  ErrorState,
  Icon,
  LineItemGrid,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
  lineTotalOf,
  type GridLine,
} from '@whinerp/ui';
import { formatDate, formatMoney } from '@whinerp/contracts/format';
import { addDecimals } from '@whinerp/contracts/money';
import { useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';
import { defineScreen } from '../types.js';
import { ApprovalBanner } from './approval-banner.js';
import { QuoteDocumentsPanel } from './quote-documents-panel.js';
import { QuoteTermsPanel } from './quote-terms-panel.js';

/**
 * SC-14 Quote builder.
 *
 * Editor on the left, the document as it will be read on the right. The
 * preview is not decoration: a quote is a thing a customer receives, and a
 * builder that only shows a grid lets somebody send a document they have never
 * looked at.
 *
 * The grid's footer updates as you type, from the same arithmetic the database
 * performs. When the server's totals arrive they replace the optimistic ones -
 * the local figure answers "what will this be", the server's answers "what is
 * this", and only one of them is authoritative.
 */

type Quote = GetQuoteResult;

function QuoteBuilderScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const queryClient = useQueryClient();
  const params = useParams();
  const quoteId = params['qid'] ?? '';
  const quoting = useEntitlement('F-047');

  const [lines, setLines] = useState<GridLine[] | null>(null);
  const [dirty, setDirty] = useState(false);

  const quote = useQuery<Quote, ApiError>({
    queryKey: ['quote', quoteId],
    queryFn: () => api.getQuote({ path: { quoteId } }),
  });

  useEffect(() => {
    if (!quote.data || dirty) return;
    setLines(
      quote.data.lines.map((line) => ({
        lineId: line.lineId,
        productId: line.productId,
        description: line.description,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        discountPercent: line.discountPercent,
        optional: line.optional,
        unitId: line.unitId,
        productBaseUnitId: line.productBaseUnitId,
      })),
    );
  }, [quote.data, dirty]);

  // Units of measure, only where they are switched on (F-161).
  const measuring = useEntitlement('F-161');
  const units = useQuery<ListUnitsResult, ApiError>({
    queryKey: ['units'],
    queryFn: () => api.listUnits({}),
    enabled: measuring.enabled,
  });

  const save = useMutation({
    mutationFn: (next: GridLine[]) =>
      api.saveQuoteLines({
        path: { quoteId },
        body: {
          lines: next.map((line, index) => ({
            lineId: line.lineId,
            productId: line.productId,
            description: line.description,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            discountPercent: line.discountPercent,
            optional: line.optional,
            unitId: line.unitId ?? null,
            sortOrder: index,
          })),
        },
      }),
    onSuccess: (result) => {
      setDirty(false);
      queryClient.setQueryData(['quote', quoteId], result);
      toast.show({ tone: 'success', title: t('state.saved') });
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  const send = useMutation({
    mutationFn: () => api.sendQuote({ path: { quoteId }, body: {} }),
    onSuccess: (result) => {
      queryClient.setQueryData(['quote', quoteId], result);
      void queryClient.invalidateQueries({ queryKey: ['deal-board'] });
      toast.show({ tone: 'success', title: t('quote.sent'), body: result.number });
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  // Switched off is not broken. F-047 is off by default, and a workspace that
  // has not turned quoting on should be told where the switch is rather than
  // shown an error about a request it never meant to make.
  if (quoting.state === 'off' || quoting.state === 'plan-locked') {
    return (
      <CapabilityUnavailable
        kind={quoting.state === 'off' ? 'module-off' : 'plan-locked'}
        capability={t('quote.title')}
        title={t('capability.moduleOff.title')}
        body={t('capability.moduleOff.body', { values: { capability: t('quote.title') } })}
        action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
      />
    );
  }

  // The error check comes first. Putting `lines === null` before it renders a
  // loading state forever when the request failed, because the effect that
  // fills `lines` never runs without data - a screen that spins instead of
  // saying what went wrong.
  if (quote.isError) return <QuoteBuilderError error={quote.error} retry={() => void quote.refetch()} />;
  if (quote.isPending || lines === null) return <QuoteBuilderLoading />;

  const data = quote.data;
  const money = (amount: string): string =>
    formatMoney(amount, format, { currency: data.currency });

  return (
    <div data-component="QuoteBuilder" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center gap-3">
        <PageHeading>{data.number}</PageHeading>
        <span
          data-quote-status={data.status}
          className={
            data.editable
              ? 'inline-flex h-chip items-center rounded-chip bg-sunken px-2 text-meta text-ink-600'
              : 'inline-flex h-chip items-center rounded-chip bg-success-tint px-2 text-meta text-success-text'
          }
        >
          {data.editable ? t('quote.title') : t('quote.sent')}
        </span>

        {/* Where it started, as provenance: editing the template later does
            not reach this quote (F-158). */}
        {data.startedFrom ? (
          <span data-quote-started-from className="text-meta text-ink-meta">
            {t('quote.startedFrom', {
              values: { template: data.startedFrom.name, version: String(data.startedFrom.version) },
            })}
          </span>
        ) : null}

        {/* Out of date, said in words on the document itself.
            The validity date was stored and shown to the customer and honoured
            by nothing, so a quote that lapsed last March still read as an
            ordinary one. */}
        {data.lapsed ? (
          <span
            data-quote-lapsed
            className="inline-flex h-chip items-center rounded-chip bg-warning-tint px-2 text-meta text-warning-text"
          >
            {t('quote.lapsed')}
          </span>
        ) : data.validUntil ? (
          <span data-quote-valid-until className="text-meta text-ink-meta">
            {t('quote.validUntil')}: {formatDate(data.validUntil, format)}
          </span>
        ) : null}

        <span className="ms-auto flex items-center gap-2">
          {data.editable ? (
            <>
              <Button
                intent="secondary"
                data-save-lines
                disabled={save.isPending || !dirty}
                onClick={() => save.mutate(lines)}
              >
                {t('action.save')}
              </Button>
              {/* Not sendable while it is out of date. The server refuses it
                  either way; the button says so rather than letting somebody
                  press it and read a refusal. */}
              <Button
                intent="primary"
                icon="PaperPlaneTilt"
                data-send-quote
                disabled={send.isPending || dirty || lines.length === 0 || data.lapsed}
                {...(data.lapsed
                  ? {
                      title: t('quote.lapsed.body', {
                        values: { date: data.validUntil ?? '' },
                      }),
                    }
                  : {})}
                onClick={() => send.mutate()}
              >
                {t('quote.send')}
              </Button>
            </>
          ) : null}
        </span>
      </header>

      {/* What the customer decided, as it stood when they decided (F-159). */}
      {data.decision ? (
        <p
          data-quote-decision={data.decision.decision}
          className={
            data.decision.decision === 'accepted'
              ? 'rounded-control bg-success-tint px-3 py-2 text-dense text-success-text'
              : 'rounded-control bg-sunken px-3 py-2 text-dense text-ink'
          }
        >
          {t(data.decision.decision === 'accepted' ? 'quote.decision.accepted' : 'quote.decision.declined', {
            values: {
              person: data.decision.contactName ?? t('quote.decision.someone'),
              date: formatDate(data.decision.decidedAt, format),
              total: formatMoney(data.decision.total, format, { currency: data.decision.currency }),
              lines: String(data.decision.chosenLines),
              reason: data.decision.reason ?? '',
            },
          })}
        </p>
      ) : null}

      {/* Who has to agree to this discount, and who did (F-049). Above the
          editor, because it is the thing that decides whether Send will work. */}
      <ApprovalBanner quoteId={quoteId} />

      {/* Printing it from a template, with what would stop that shown first
          (F-050). */}
      <QuoteDocumentsPanel quoteId={quoteId} defaultTemplateId={data.documentTemplateId} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[60%_40%]">
        <Panel data-region="editor">
          <div className="flex flex-col gap-3">
            <SectionLabel>{t('quote.lines')}</SectionLabel>
            <LineItemGrid
              lines={lines}
              editable={data.editable}
              discountPercent={data.totals.discountPercent}
              // The server's totals once they land; the optimistic ones while
              // somebody is typing.
              serverTotals={
                dirty
                  ? undefined
                  : {
                      subtotal: data.totals.subtotal,
                      optionalTotal: data.totals.optionalTotal,
                      discountAmount: data.totals.discountAmount,
                      total: data.totals.total,
                    }
              }
              money={money}
              units={
                units.data && units.data.units.length > 0
                  ? {
                      all: units.data.units,
                      label: t('quote.unit'),
                      converted: (amount, base) => t('quote.unitConverted', { values: { amount, base } }),
                    }
                  : undefined
              }
              onChange={(next) => {
                setLines(next);
                setDirty(true);
              }}
              labels={{
                description: t('quote.description'),
                quantity: t('quote.quantity'),
                unitPrice: t('quote.unitPrice'),
                discount: t('quote.discount'),
                lineTotal: t('quote.lineTotal'),
                optional: t('quote.optional'),
                addLine: t('quote.addLine'),
                removeLine: (description) =>
                  t('quote.removeLine', { values: { description } }),
                keyboardHint: t('quote.keyboardHint'),
                subtotal: t('quote.subtotal'),
                optionalTotal: t('quote.optionalTotal'),
                discountLabel: t('quote.discountLabel'),
                total: t('quote.total'),
                readOnly: t('quote.readOnly'),
                rowActions: t('quote.rowActions'),
              }}
            />
          </div>
        </Panel>

        {/* The document as the customer will read it. */}
        <Panel data-region="preview">
          <article className="flex flex-col gap-3">
            <SectionLabel>{t('quote.preview')}</SectionLabel>
            <h2 className="serif-moment text-h2 text-ink">{data.name || data.number}</h2>
            {data.validUntil ? (
              <Num className="text-meta text-ink-meta">
                {t('quote.validUntil')}: {formatDate(data.validUntil, format)}
              </Num>
            ) : null}

            <ul className="flex flex-col">
              {lines.map((line, index) => (
                <li
                  key={line.lineId ?? `preview-${String(index)}`}
                  className="flex items-baseline justify-between gap-3 border-t border-t-rule py-1 first:border-t-0"
                >
                  <span className="min-w-0 flex-1 truncate text-dense text-ink">
                    {line.description || '—'}
                    {line.optional ? (
                      <span className="ms-2 text-meta text-ink-meta">({t('quote.optional')})</span>
                    ) : null}
                  </span>
                  <Num align="end" className="text-dense text-ink">
                    {money(lineTotalFor(line))}
                  </Num>
                </li>
              ))}
            </ul>

            {/*
              What this quote earns, when the reader may know (G-103).

              From the SAVED lines, not the grid being edited. The margin is
              the server's arithmetic against the server's costs; computing it
              here from unsaved edits would be a second implementation, and the
              two would disagree the first time a rounding rule changed. It
              moves when the lines are saved, which is when the price becomes
              a fact rather than a keystroke.

              Three answers kept apart. A figure; "no cost entered, so the
              margin is unknown"; and nothing at all for somebody who may not
              see it. Showing zero for either of the last two is the defect:
              it is the most flattering possible wrong answer, on the screen
              where somebody decides whether they can afford a discount.
            */}
            {data.marginVisible ? (
              <p
                data-quote-margin
                className="flex items-baseline justify-between gap-3 border-t border-t-rule pt-2 text-meta text-ink-meta"
              >
                <span>{t('quote.margin')}</span>
                {data.lines.some((line) => line.margin && !line.margin.known) ? (
                  <span data-margin-unknown className="text-warning-text">
                    {t('quote.margin.unknown')}
                  </span>
                ) : (
                  <Num align="end" data-margin-amount className="text-ink">
                    {money(
                      data.lines.reduce(
                        (sum, line) => addDecimals(sum, line.margin?.amount ?? '0'),
                        '0',
                      ),
                    )}
                  </Num>
                )}
              </p>
            ) : null}

            <p className="flex items-baseline justify-between gap-3 border-t border-t-rule-strong pt-2">
              <span className="text-body font-medium text-ink">{t('quote.total')}</span>
              <Num align="end" className="text-body font-medium text-ink">
                {money(data.totals.total)}
              </Num>
            </p>

            {data.terms ? <p className="whitespace-pre-wrap text-meta text-ink-meta">{data.terms}</p> : null}

            {!data.editable ? (
              <p className="flex items-center gap-2 text-meta text-success-text">
                <Icon name="CheckCircle" size="inline" decorative />
                {t('quote.readOnly')}
              </p>
            ) : null}
          </article>
        </Panel>
      </div>

      {/* Terms and validity. A template fills them in, and they stay editable
          (F-158). */}
      <QuoteTermsPanel quote={data} />
    </div>
  );
}

/** Re-exported from the grid so the preview and the grid agree by construction. */
function lineTotalFor(line: GridLine): string {
  return lineTotalOf(line);
}

function QuoteBuilderEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="FileText"
      title={t('quote.empty.title')}
      body={t('quote.empty.body')}
      primary={{ label: t('empty.action.addLine'), icon: 'Plus', href: '/deals' }}
    />
  );
}

function QuoteBuilderLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-lg" />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[60%_40%]">
        <Skeleton className="h-block-xl w-full" />
        {/* A placeholder document, not a blank panel: the preview's shape is
            part of what the screen is for. */}
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function QuoteBuilderError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This quote could not be loaded"
      body={error instanceof Error ? error.message : 'The quote did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-14',
  route: '/deals/:id/quotes/:qid',
  surface: 'page',
  title: 'Quote builder',
  features: ['F-047', 'F-048', 'F-049', 'F-050', 'F-161'],
  entitlement: 'F-047',
  reachedBy: [{ kind: 'screen', from: 'SC-13', how: 'a quote on the deal' }],
  // 06_Component_Library names no line-item grid, so it is declared as a
  // screen element rather than borrowed from a list it is not on. The check
  // still watches it: an element that stops rendering fails just the same.
  components: [],
  elements: ['QuoteBuilder', 'LineItemGrid'],
  sampleRecord: [
    { objectKey: 'deal', param: 'id' },
    { objectKey: 'quote', param: 'qid' },
  ],
  states: {
    empty: QuoteBuilderEmpty,
    loading: QuoteBuilderLoading,
    error: QuoteBuilderError,
  },
  component: QuoteBuilderScreen,
});

export default screen;
